// @architect-approved: INFRASTRUCTURE - Route loader does not follow component identity pattern (no Self const)
/**
 * RouteLoaderEncoded.tsx
 * @architect-approved: INFRASTRUCTURE - Route loader does not follow component identity pattern (no Self const)
 *
 * PURPOSE: Runtime database-driven routing (NO static routes.json)
 * Route Format: /component/{component_external_id}
 *
 * ARCHITECTURE: Netflix/Citrix Streaming Model
 * - Database IS the routing manifest (Component_Registry)
 * - Warehouse IS the physical truth (RegisteredComponents/)
 * - Runtime queries database, loads component on-demand
 * - NO prebuild generation, NO agent compulsion target
 *
 * CYPHER STRUCTURE:
 * - Component: 2A-{ID}-{Dept}-{DeptGroup}-{Hierarchy}-{Type}
 * - Example: /component/2A-07615-07-31-4-TAB
 *
 * BREADCRUMB DISPLAY:
 * - Routes are cyphers (machine-readable)
 * - UI shows Friendly_Name from Component_Registry metadata
 * - Breadcrumbs built from hierarchical relationships
 *
 * Created: 2026-03-13
 * Updated: 2026-03-13 - Removed static routing, added runtime DB query
 */

import { Box, CircularProgress } from '@mui/material';
import { lazy, Suspense } from 'react';
import { Route } from 'react-router-dom';
import generatedRoutes from './generated-routes.json';

// Import all RegisteredModules, tabs, and directories using Vite's glob import
// Updated: 2026-06-05 — RegisteredComponents renamed to RegisteredModules (clarity)
// TGC domain
const registeredModules = import.meta.glob('./TGC/RegisteredModules/*.tsx');
const registeredBinders = import.meta.glob('./TGC/RegisteredBinders/*.tsx');
// EXCLUDE (2026-10-28): mobuni_* files are React Native source snapshots
// (Mobile App Developer Portal reference only) - they import 'react-native',
// which isn't installed at the web project root, so Rollup fails to resolve
// it if this glob ever includes them. Matches the same exclude in
// componentResolver.ts and tsconfig.json.
const registeredTabs = import.meta.glob([
    './TGC/RegisteredTabs/*.tsx',
    '!./TGC/RegisteredTabs/mobuni_*.tsx',
]);
const registeredTheaterScreens = import.meta.glob('./TGC/RegisteredTheaterScreens/*.tsx');
const registeredHierComposer = import.meta.glob('./TGC/RegisteredHierComposer/*.tsx');
const allTabs = import.meta.glob('./TGC/**/tabs/*.tsx', { eager: false });
// EAGER (2026-09-08): directory host templates (nav_*, gate_*) are a small
// shared set rendering database-driven buttons/cards/menus - eager loading
// closes the async gap between host mount and dirSeq/manifest availability.
const tgcDirectories = import.meta.glob('./TGC/RegisteredDirectories/*.tsx', { eager: true });
const marketingSites = import.meta.glob('./TGC/RegisteredMarketingSites/*.tsx');
// CAB domain (Caboose)
const cabMarketingSites = import.meta.glob('./CAB/MarketingSites/*.tsx');
const cabCompanyWebsite = import.meta.glob('./CAB/CompanyWebsite/*.tsx');
// LCV domain
const lcvDirectories = import.meta.glob('./LCV/RegisteredDirectories/*.tsx', { eager: true });
const lcvComponents = import.meta.glob('./LCV/RegisteredComponents/*.tsx');

// Loading fallback component
const LoadingFallback = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
        }}
    >
        <CircularProgress />
    </Box>
);

// Cache for generated routes — prevents re-processing 772 routes on every App.tsx render
let routeCache: JSX.Element[] | null = null;

/**
 * REMOVED: getComponentMetadata() - Dead code, never called
 * REMOVED: getFriendlyRouteName() - Dead code, never called
 * REMOVED: getBreadcrumbPath() - Dead code, never called
 *
 * These functions referenced deprecated /api/component-registry endpoint
 * and were not used anywhere in the codebase.
 */

/**
 * REMOVED: Legacy RuntimeComponentLoader - unused
 *
 * Old pattern used dynamic imports with @vite-ignore which violates
 * Vite's static analysis requirements. All routing now handled by
 * generateEncodedRoutes() which uses proper import.meta.glob() pattern.
 *
 * See: scripts/validate_vite_glob_imports.py for enforcement
 */

/**
 * Generate routes from generated-routes.json
 * Creates direct routes for RegisteredComponents and tabs
 *
 * PERFORMANCE: Cached after first execution — prevents re-processing 772 routes on every render.
 * App.tsx renders frequently (state changes, context updates) — route generation is expensive.
 * Cache invalidation: Only on hot reload or app restart.
 */
export function generateEncodedRoutes() {
    // Return cached routes if already generated (prevents 772-route iteration on every App.tsx render)
    if (routeCache) {
        return routeCache;
    }

    // @performance: Logs commented during production use - uncomment for debugging
    // console.log('[RouteLoader] Loading routes from generated-routes.json');
    // console.log('[RouteLoader] Total routes:', generatedRoutes.totalRoutes);

    const routes: JSX.Element[] = [];

    // Generate routes for all components that have a directRoute (tsxFileName)
    // directRoute IS the tsxFileName — if it exists, the component is routable
    generatedRoutes.routes
        .filter((route: any) => route.directRoute)
        .forEach((route: any) => {
            // Build the import path from componentPath
            const importPath = `./${route.componentPath}.tsx`;

            // Get the lazy component from glob imports
            const componentLoader =
                registeredModules[importPath] ||
                registeredBinders[importPath] ||
                registeredTabs[importPath] ||
                registeredTheaterScreens[importPath] ||
                registeredHierComposer[importPath] ||
                allTabs[importPath] ||
                tgcDirectories[importPath] ||
                marketingSites[importPath] ||
                cabMarketingSites[importPath] ||
                cabCompanyWebsite[importPath] ||
                lcvDirectories[importPath] ||
                lcvComponents[importPath];

            if (!componentLoader) {
                console.warn(`[RouteLoader] Component not found in glob imports: ${importPath}`);
                return;
            }

            // componentLoader is a () => Promise<Module> function for lazy-glob
            // folders, or already the resolved module object for eager-glob folders
            // (directory hosts: tgcDirectories/lcvDirectories above). Handle both.
            // Direct mount, no retry/reload wrapper (removed 2026-10-23): onClick ->
            // navigate -> mount must be a direct path with nothing in between, per
            // platform principle.
            const Component = lazy(() =>
                typeof componentLoader === 'function'
                    ? (componentLoader() as Promise<{ default: React.ComponentType<any> }>)
                    : Promise.resolve(componentLoader as { default: React.ComponentType<any> }),
            );

            // Route key uses directRoute (filename-based)
            const routeKey = route.directRoute;

            // AuthGuard at App.tsx level handles all authentication
            // No need for per-route wrappers - removed redundant RouteSecurityWrapper

            routes.push(
                <Route
                    key={routeKey}
                    path={route.directRoute}
                    element={
                        <Suspense fallback={<LoadingFallback />}>
                            <Component />
                        </Suspense>
                    }
                />,
            );
        });

    // @performance: Log commented during production use - uncomment for debugging
    // console.log('[RouteLoader] Generated', routes.length, 'direct routes');

    // Cache the routes to prevent re-generation on every render
    routeCache = routes;
    return routes;
}

export default generateEncodedRoutes;
